import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, Eye, User, Share2, ArrowLeft, MapPin, Camera, Bookmark, Twitter, Linkedin, Facebook, ChevronRight } from 'lucide-react';
import { motion, useScroll, useSpring, AnimatePresence } from 'framer-motion';
import { SkeletonArticle } from '../components/LoadingSpinner';
import PremiumContentGate from '../components/PremiumContentGate';
import MagazineReader from '../components/MagazineReader';
import { useAuth } from '../context/AuthContext';
import { useArticle, useArticles } from '../hooks/useArticles';
import { formatReadingTime } from '../utils/formatters';

const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { data: article, isLoading, error } = useArticle(slug);
  const { data: allArticles } = useArticles();
  const [isReaderOpen, setIsReaderOpen] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-full max-w-4xl px-4"><SkeletonArticle /></div>
      </div>
    );
  }

  const displayArticle = article;

  if (!displayArticle || (error && slug !== "atlantis-the-palm-dubai")) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-center px-4">
        <div>
          <h1 className="text-4xl font-serif font-bold mb-4">Article Not Found</h1>
          <button onClick={() => navigate("/")} className="px-8 py-3 bg-black text-white font-medium hover:opacity-80">Return Home</button>
        </div>
      </div>
    );
  }

  const isPremium = !!Number(displayArticle.is_premium);
  const canReadPremium = isAuthenticated && user?.is_premium && user?.subscription_status === "active";
  const isLocked = displayArticle?.is_locked || (isPremium && !canReadPremium);

  const openMagazineReader = () => { if (allArticles?.length > 0) setIsReaderOpen(true); };
  const closeMagazineReader = () => setIsReaderOpen(false);

  const shareArticle = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: displayArticle.title, url: window.location.href });
      } catch (err) { console.log(err); }
    } else {
      navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    }
  };

  // --- Enhanced Share Logic ---
  const shareLinks = {
    x: `https://x.com/intent/tweet?text=${encodeURIComponent(displayArticle.title)}&url=${encodeURIComponent(window.location.href)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(displayArticle.title + " " + window.location.href)}`
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: displayArticle.title,
          text: displayArticle.dek || displayArticle.subtitle,
          url: window.location.href,
        });
      } catch (err) {
        console.log("Error sharing", err);
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    setShowShareToast(true);
    setTimeout(() => setShowShareToast(false), 3000);
  };

 
  const cleanHTML = (html) => {
    if (!html) return '';
    return html
      .replace(/<img/g, '<img class="w-full h-auto my-12 rounded-lg shadow-xl"');
  };

  const slugify = (text) => {
    return text
      ?.toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')
      .replace(/[^\w-]+/g, '')
      .replace(/--+/g, '-')
      || 'staff';
  };

  return (
    <div className="min-h-screen bg-white antialiased selection:bg-black selection:text-white">
      {/* Scroll Progress */}
      <motion.div className="fixed top-0 left-0 right-0 h-1 bg-black z-[100] origin-left" style={{ scaleX }} />

      {/* Share Toast */}
      <AnimatePresence>
        {showShareToast && (
          <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 50, opacity: 0 }}
            className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[100] bg-black text-white px-6 py-3 rounded-full text-xs font-bold tracking-widest uppercase">
            Link Copied to Clipboard
          </motion.div>
        )}
      </AnimatePresence>

      {/* Hero Section: Refined Split Layout */}
     <header className="relative w-full border-b border-gray-100 bg-[#F9F9F9]">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-stretch overflow-hidden">
          
          {/* Left: Text Content - Matches Image Height Exactly */}
          <div className="flex flex-col justify-center px-8 py-12 lg:px-20 lg:py-16">
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              animate={{ opacity: 1, x: 0 }} 
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <div className="flex items-center space-x-3 text-[10px] font-black tracking-[0.4em] uppercase text-gray-400 mb-6">
                <span className="w-6 h-[1px] bg-gray-300"></span>
                <span>{displayArticle.category || "Lifestyle"}</span>
                {isPremium && (
                  <span className="bg-black text-white px-2 py-0.5 rounded-sm text-[8px] tracking-widest">
                    PREMIUM
                  </span>
                )}
              </div>
              
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] text-gray-900 mb-6 tracking-tight">
                {displayArticle.title}
              </h1>

              {(displayArticle.dek || displayArticle.subtitle) && (
                <p className="text-lg lg:text-xl text-gray-500 font-light leading-relaxed mb-8 max-w-lg">
                  {displayArticle.dek || displayArticle.subtitle}
                </p>
              )}

              <div className="flex flex-wrap gap-y-4 gap-x-8 items-center text-[10px] uppercase tracking-[0.25em] font-bold text-gray-600 border-t border-gray-200 pt-8">
  {/* Author - Always visible */}
  <Link 
  to={`/author/${slugify(displayArticle.author_name || displayArticle.author?.name)}`} 
  className="flex items-center group cursor-pointer"
>
  <div className="p-1 rounded-full border border-transparent group-hover:border-gray-200 transition-all mr-1">
    <User className="text-gray-900 group-hover:text-blue-600 transition-colors" size={14} /> 
  </div>
  <span className="border-b border-transparent group-hover:border-gray-900 transition-all">
    {displayArticle.author_name || displayArticle.author?.name || "Staff"}
  </span>
</Link>

  {/* Reading Time - Always visible */}
  <div className="flex items-center">
    <Clock className="mr-2 text-gray-900" size={14} /> 
    {displayArticle.reading_time || "5"} MIN
  </div>

  {/* Date - Uses published_at, fallback to created_at */}
  <div className="flex items-center">
    <Calendar className="mr-2 text-gray-900" size={14} />
    <span>
      {displayArticle.published_at
        ? formatDate(displayArticle.published_at)
        : formatDate(displayArticle.created_at)}
    </span>
  </div>

  {/* Photographer - Conditional */}
  {displayArticle.photographer && (
    <div className="flex items-center">
      <Camera className="mr-2 text-gray-900" size={14} />
      <span>Photo by {displayArticle.photographer}</span>
    </div>
  )}

  {/* Location - Conditional */}
  {displayArticle.location && (
    <div className="flex items-center border-l border-gray-200 pl-6">
      <MapPin className="mr-2 text-gray-900" size={14} />
      <span>{displayArticle.location}</span>
    </div>
  )}
</div>
            </motion.div>
          </div>

          {/* Right: Hero Image - Maintains Aspect Ratio without distortion */}
          <div className="relative bg-white flex items-center justify-center overflow-hidden">
            <motion.div 
              className="w-full h-full"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1 }}
            >
              <img 
                src={displayArticle.hero_image || displayArticle.heroImage} 
                alt={displayArticle.title}
                /* aspect-square or aspect-[4/5] ensures a stable height that the text side will match */
                className="w-full h-full object-cover object-center transition-transform duration-[2s] hover:scale-105"
                onError={(e) => (e.target.src = "/placeholder-article.jpg")}
              />
            </motion.div>
            {/* Subtle Overlay to unify the image with the site aesthetic */}
            <div className="absolute inset-0 bg-black/5 pointer-events-none" />
          </div>
        </div>
      </header>
      {/* Main Content Area */}
      <main className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16 py-20">
        
        {/* Left Side: Sticky Meta/Social */}
        <div className="lg:col-span-2">
          <div className="sticky top-32 space-y-12">
            <div className="space-y-4">
                <div className="flex flex-col space-y-4">
                  <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-600">Share Story</h4>
                  
                  {/* X (formerly Twitter) */}
                  <a 
                    href={shareLinks.x} 
                    target="_blank" 
                    rel="noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-100 hover:bg-black hover:text-white transition-all group"
                    title="Share on X"
                  >
                    <span className="font-sans font-bold text-sm">X</span>
                  </a>

                  {/* Facebook */}
                  <a 
                    href={shareLinks.facebook} 
                    target="_blank" 
                    rel="noreferrer"
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-100 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-all"
                    title="Share on Facebook"
                  >
                    <Facebook size={16} fill="currentColor" />
                  </a>

                  {/* Native Share / Copy Link */}
                  <button 
                    onClick={handleNativeShare}
                    className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-100 hover:bg-black hover:text-white transition-all"
                    title="Copy Link or Share"
                  >
                    <Share2 size={16} />
                  </button>
                </div>
            </div>
          </div>
        </div>

        {/* Center: Article Content */}
        <div className="lg:col-span-8">
          <article className="prose prose-stone prose-lg md:prose-xl max-w-none 
            prose-p:text-gray-700 prose-p:leading-[1.8] prose-p:mb-10
            prose-headings:font-serif prose-headings:font-medium
            prose-blockquote:border-l-4 prose-blockquote:border-black prose-blockquote:bg-gray-50 prose-blockquote:py-8 prose-blockquote:px-10 prose-blockquote:italic prose-blockquote:text-gray-900">
            
            {isLocked ? (
              <PremiumContentGate article={displayArticle} showPreview={true} />
            ) : (
              <div 
                className="article-body-render dropcap"
                dangerouslySetInnerHTML={{ __html: cleanHTML(displayArticle.content || displayArticle.body) }} 
              />
            )}
          </article>

          {/* Tags */}
          {displayArticle.tags?.length > 0 && (
            <div className="mt-20 pt-10 border-t border-gray-100 flex flex-wrap gap-3">
              {displayArticle.tags.map(tag => (
                <Link key={tag} to={`/search?q=${tag}`} className="px-4 py-2 bg-gray-50 text-[10px] font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-all">#{tag}</Link>
              ))}
            </div>
          )}

          {/* Footer Navigation */}
          {/* <div className="mt-24 py-12 border-y border-gray-100 flex flex-col md:flex-row items-center justify-between gap-8">
            <button onClick={() => navigate(-1)} className="group flex items-center text-[10px] font-black uppercase tracking-[0.3em]">
              <ArrowLeft className="mr-3 group-hover:-translate-x-2 transition-transform" size={16} /> Previous Story
            </button>
            <button onClick={openMagazineReader} className="px-8 py-4 bg-black text-white text-[10px] font-black uppercase tracking-[0.3em] hover:scale-105 transition-transform">
              Launch Digital Edition
            </button>
          </div> */}
        </div>
      </main>

      <MagazineReader articles={allArticles} isOpen={isReaderOpen} onClose={closeMagazineReader}
        initialPageIndex={allArticles ? allArticles.findIndex((a) => a.slug === displayArticle.slug) * 2 + 1 : 0}
      />

      <style dangerouslySetInnerHTML={{ __html: `
        .dropcap > p:first-of-type::first-letter {
          float: left;
          font-family: serif;
          font-size: 4.5rem;
          line-height: 3.5rem;
          padding-top: 4px;
          padding-right: 12px;
          color: #000;
        }
      `}} />
    </div>
  );
};

export default ArticlePage;